import { useApolloClient, useQuery } from '@apollo/client';
import { Box, Button, Container, Paper, TextField, Typography, styled } from '@mui/material';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useLoginUser } from '~/api/users';
import { PasswordField } from '~/components/forms/password-field';
import { Link } from '~/components/link';
import { AppContextDocument, resolveTargetNetwork } from '~/contexts';
import logo from '~/images/fc-logo.svg';
import { resetWebsocketConnection } from '~/lib/apollo';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  backgroundColor: theme.palette.primary.main,
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0, 2),
  padding: theme.spacing(6),
  border: '1px solid #E6E6E6',
  borderRadius: '2px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Logo = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 40%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '75%',
  padding: theme.spacing(0, 0, 1),
}));

const messageSx = {
  textTransform: 'uppercase' as const,
  color: 'text.primary',
  px: 0,
  pt: 0,
  pb: 2,
};

const Form = styled('form')(({ theme }) => ({
  width: '100%', // Fix IE 11 issue.
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const FormInput = styled('div')(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
}));

export const Login = () => {
  const { t } = useTranslation();

  const client = useApolloClient();

  const { state } = useLocation() as { state: { from?: string } | null | undefined };
  const from = state?.from;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { data } = useQuery(AppContextDocument, {
    fetchPolicy: 'cache-first',
  });

  const currentUser = data?.currentUser;

  const [login, loginState] = useLoginUser();

  useEffect(() => {
    if (!currentUser) return;
    resetWebsocketConnection();
    void client.resetStore();
  }, [client, currentUser]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await login({ variables: { input: { email, password } } }).catch(() => {
      /*no-op*/
    });
  };

  if (currentUser) {
    const targetNetwork = resolveTargetNetwork(currentUser);
    return <Navigate to={from || `/networks/${targetNetwork?.id}`} replace />;
  }

  return (
    <Root>
      <Helmet title={t('pages.auth.login.sign_in')} />
      <Container maxWidth="sm">
        <FormPaper elevation={5}>
          <Logo>
            <img alt="FanConnect" src={logo} />
          </Logo>
          <Typography component="h1" variant="h5" gutterBottom sx={messageSx}>
            {t('pages.auth.login.title')}
          </Typography>
          {loginState.error && (
            <Typography align="center" color="error" role="alert">
              {loginState.error.message}
            </Typography>
          )}
          <Form onSubmit={onSubmit}>
            <FormInput>
              <TextField
                label={t('common.labels.email')}
                autoComplete="username"
                variant="outlined"
                margin="dense"
                fullWidth
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
            </FormInput>
            <FormInput>
              <PasswordField
                kind="current"
                label={t('pages.auth.login.password')}
                fullWidth
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </FormInput>
            <FormInput>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                color="primary"
                size="medium"
                disabled={!email || !password || loginState.loading}
              >
                {t('pages.auth.login.sign_in')}
              </Button>
            </FormInput>
            <Typography align="center">
              <Link to="/forgot-password">{t('pages.auth.login.forgot_password')}</Link>
            </Typography>
          </Form>
        </FormPaper>
        <Box flex="1 1 40%" flexGrow={1} />
      </Container>
    </Root>
  );
};
